import { useEffect } from 'react';
// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in US Dollars (USD).
// ============================================================
export type ProductKey = 'megapack';
export const PRODUCTS: Record<ProductKey, {
  id: string;
  name: string;
  value: number;
  currency: 'USD';
  checkoutUrl: string;
}> = {
  megapack: {
    id: 'megapack-funcional-365',
    name: 'MEGAPACK FUNCIONAL 365',
    value: 16,
    currency: 'USD',
    checkoutUrl: 'https://megapack-funcional-365.impultienda.ar/checkout'
  }
};
const PIXEL_ID = '25378909385106188';
// Safely access fbq if available
const getFbq = (): ((...args: any[]) => void) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as any;
  return typeof w.fbq === 'function' ? w.fbq : null;
};
// ============================================================
// Tracking helpers — use these from anywhere in the app
// ============================================================
export const trackInitiateCheckout = (productKey: ProductKey) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'InitiateCheckout', {
    value: p.value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: p.value
    }],
    num_items: 1
  });
};
export const trackPurchase = (productKey: ProductKey, overrideValue?: number) => {
  const fbq = getFbq();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' ? overrideValue : p.value;
  fbq('track', 'Purchase', {
    value,
    currency: p.currency,
    content_ids: [p.id],
    content_name: p.name,
    content_type: 'product',
    contents: [{
      id: p.id,
      quantity: 1,
      item_price: value
    }],
    num_items: 1
  });
};
// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=1[&value=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    // Initialize fbq queue immediately (cheap — just sets up the stub)
    // so any early CTA click can still queue an InitiateCheckout event.
    if (!w.fbq) {
      const n: any = w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      w.fbq('init', PIXEL_ID);
      w.fbq('track', 'PageView');
      // Defer the actual network load until the browser is idle so it never
      // competes with the hero paint. Falls back to setTimeout for Safari.
      const loadScript = () => {
        if (document.getElementById('fb-pixel-script')) return;
        const script = document.createElement('script');
        script.id = 'fb-pixel-script';
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
      };
      if ('requestIdleCallback' in w) {
        w.requestIdleCallback(loadScript, {
          timeout: 2500
        });
      } else {
        setTimeout(loadScript, 1200);
      }
    }
    // Handle the Purchase return URL:
    // e.g. https://landing.com/?purchase=1          → fires Purchase
    // e.g. https://landing.com/?purchase=1&value=16 → overrides the value
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase')) {
        const valueParam = params.get('value');
        const overrideValue = valueParam ? Number(valueParam) : undefined;
        let attempts = 0;
        // Defer until the pixel script has had a chance to load
        const fire = () => {
          if (getFbq()) {
            trackPurchase('megapack', overrideValue);
            // Clean the query string so a refresh doesn't double-fire
            const url = new URL(window.location.href);
            url.searchParams.delete('purchase');
            url.searchParams.delete('value');
            window.history.replaceState({}, '', url.toString());
            return;
          }
          if (attempts < 20) {
            attempts += 1;
            setTimeout(fire, 300);
          }
        };
        fire();
      }
    } catch {
      // Tracking must never break the landing page.
    }
  }, []);
  return null;
}